import { memo } from 'react';
import { Group, Circle, Ellipse, Arc, Line, Rect, Shape } from 'react-konva';

export const BatelkaCharacter = memo(function BatelkaCharacter({ x, y, scaleX, scaleY, rotation, opacity, fill, customColors, onClick, onDragEnd, onTransformEnd, draggable, isSelected }) {
  const c = customColors || {};
  const skinColor = c.skinColor || '#fdd9b5';
  const skinShadow = '#f0c8a0';
  const hairColor = c.hairColor || '#8b4513';
  const hairDark = '#6b3410';
  const leotardColor = c.dressColor || fill || '#f8a4c8';
  const tutuColor = c.dressColor || '#f48fb1';
  const tutuLight = '#f8bbd0';
  const tightsColor = '#fce4ec';
  const shoeColor = c.shoeColor || '#f48fb1';
  const eyeColor = c.eyeColor || '#4fc3f7';
  const pupilColor = '#1a237e';
  const lipColor = '#e57373';
  const cheekColor = '#ffab91';

  return (
    <Group
      x={x}
      y={y}
      scaleX={scaleX || 1}
      scaleY={scaleY || 1}
      rotation={rotation || 0}
      opacity={opacity ?? 1}
      draggable={draggable}
      onClick={onClick}
      onTap={onClick}
      onDragEnd={onDragEnd}
      onTransformEnd={onTransformEnd}
    >
      {/* === LEFT ARM (behind body) === */}
      <Line points={[-8, -20, -30, -35, -48, -45]} stroke={skinColor} strokeWidth={7} lineCap="round" lineJoin="round" />
      <Circle x={-48} y={-45} radius={4} fill={skinColor} />

      {/* === RIGHT ARM (behind body) === */}
      <Line points={[8, -20, 32, -50, 50, -60]} stroke={skinColor} strokeWidth={7} lineCap="round" lineJoin="round" />
      <Circle x={50} y={-60} radius={4} fill={skinColor} />

      {/* === SUPPORTING LEG (left, standing on pointe) === */}
      <Line points={[-3, 20, -5, 45, -4, 65, -3, 80]} stroke={tightsColor} strokeWidth={8} lineCap="round" lineJoin="round" />
      {/* Pointe shoe */}
      <Ellipse x={-3} y={84} radiusX={6} radiusY={4} fill={shoeColor} />
      <Line points={[-3, 80, -3, 88]} stroke={shoeColor} strokeWidth={5} lineCap="round" />
      {/* Shoe ribbon */}
      <Line points={[-8, 72, -3, 68, 2, 72]} stroke={shoeColor} strokeWidth={1.5} lineCap="round" />
      <Line points={[-7, 66, -3, 63, 1, 66]} stroke={shoeColor} strokeWidth={1.5} lineCap="round" />

      {/* === EXTENDED LEG (right, extended back) === */}
      <Line points={[3, 18, 20, 20, 42, 15, 55, 10]} stroke={tightsColor} strokeWidth={7} lineCap="round" lineJoin="round" />
      {/* Pointe shoe on extended leg */}
      <Ellipse x={58} y={9} radiusX={6} radiusY={3} fill={shoeColor} rotation={-15} />

      {/* === TUTU (multiple layers) === */}
      {/* Back tutu layer */}
      <Shape
        sceneFunc={(ctx, shape) => {
          ctx.beginPath();
          ctx.moveTo(-25, 10);
          ctx.quadraticCurveTo(-35, 25, -28, 18);
          ctx.lineTo(-20, 20);
          ctx.quadraticCurveTo(-15, 28, -8, 20);
          ctx.lineTo(0, 22);
          ctx.quadraticCurveTo(8, 28, 15, 20);
          ctx.lineTo(22, 18);
          ctx.quadraticCurveTo(30, 25, 25, 10);
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        fill={tutuColor}
        opacity={0.6}
      />
      {/* Main tutu - fan shape */}
      <Shape
        sceneFunc={(ctx, shape) => {
          ctx.beginPath();
          ctx.moveTo(-8, 8);
          // Create pleated tutu fan
          ctx.lineTo(-32, 14);
          ctx.lineTo(-30, 18);
          ctx.lineTo(-26, 14);
          ctx.lineTo(-28, 20);
          ctx.lineTo(-24, 16);
          ctx.lineTo(-22, 22);
          ctx.lineTo(-18, 16);
          ctx.lineTo(-16, 22);
          ctx.lineTo(-12, 16);
          ctx.lineTo(-10, 22);
          ctx.lineTo(-6, 16);
          ctx.lineTo(-4, 22);
          ctx.lineTo(0, 16);
          ctx.lineTo(2, 22);
          ctx.lineTo(6, 16);
          ctx.lineTo(8, 22);
          ctx.lineTo(12, 16);
          ctx.lineTo(14, 22);
          ctx.lineTo(18, 16);
          ctx.lineTo(20, 22);
          ctx.lineTo(24, 16);
          ctx.lineTo(26, 20);
          ctx.lineTo(30, 14);
          ctx.lineTo(32, 18);
          ctx.lineTo(8, 8);
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        fill={tutuColor}
      />
      {/* Top tutu layer */}
      <Shape
        sceneFunc={(ctx, shape) => {
          ctx.beginPath();
          ctx.moveTo(-6, 6);
          ctx.lineTo(-28, 12);
          ctx.quadraticCurveTo(-20, 16, -10, 14);
          ctx.quadraticCurveTo(0, 18, 10, 14);
          ctx.quadraticCurveTo(20, 16, 28, 12);
          ctx.lineTo(6, 6);
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        fill={tutuLight}
        opacity={0.7}
      />

      {/* === TORSO (leotard) === */}
      <Shape
        sceneFunc={(ctx, shape) => {
          ctx.beginPath();
          ctx.moveTo(-10, -28);
          ctx.quadraticCurveTo(-14, -10, -10, 10);
          ctx.lineTo(10, 10);
          ctx.quadraticCurveTo(14, -10, 10, -28);
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        fill={leotardColor}
      />
      {/* Leotard neckline */}
      <Line points={[-8, -28, 0, -24, 8, -28]} stroke={tutuColor} strokeWidth={2} lineCap="round" />

      {/* === HEAD === */}
      <Ellipse x={0} y={-42} radiusX={18} radiusY={20} fill={skinColor} />

      {/* Ear left */}
      <Ellipse x={-16} y={-42} radiusX={4} radiusY={6} fill={skinShadow} />
      {/* Ear right */}
      <Ellipse x={16} y={-42} radiusX={4} radiusY={6} fill={skinShadow} />

      {/* === HAIR === */}
      {/* Back hair */}
      <Shape
        sceneFunc={(ctx, shape) => {
          ctx.beginPath();
          ctx.arc(0, -48, 18, Math.PI * 0.8, Math.PI * 2.2, false);
          ctx.quadraticCurveTo(16, -36, 14, -34);
          ctx.lineTo(-14, -34);
          ctx.quadraticCurveTo(-16, -36, -18, -44);
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        fill={hairColor}
      />
      {/* Front hair/bangs */}
      <Shape
        sceneFunc={(ctx, shape) => {
          ctx.beginPath();
          ctx.moveTo(-14, -48);
          ctx.quadraticCurveTo(-12, -56, -6, -58);
          ctx.quadraticCurveTo(0, -62, 6, -58);
          ctx.quadraticCurveTo(12, -56, 14, -48);
          ctx.quadraticCurveTo(10, -52, 4, -52);
          ctx.quadraticCurveTo(0, -50, -4, -52);
          ctx.quadraticCurveTo(-10, -52, -14, -48);
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        fill={hairColor}
      />
      {/* Hair bun */}
      <Circle x={0} y={-66} radius={10} fill={hairColor} />
      <Circle x={0} y={-66} radius={8} fill={hairDark} />
      {/* Bun highlight */}
      <Circle x={-2} y={-69} radius={3} fill={hairColor} opacity={0.6} />

      {/* === FACE === */}
      {/* Left eye */}
      <Ellipse x={-7} y={-44} radiusX={5.5} radiusY={6.5} fill="white" />
      <Circle x={-6} y={-43} radius={3.5} fill={eyeColor} />
      <Circle x={-5.5} y={-43.5} radius={2} fill={pupilColor} />
      <Circle x={-4.5} y={-45} radius={1.2} fill="white" />
      {/* Left eyelash */}
      <Line points={[-12, -49, -10, -47, -7, -50]} stroke={hairDark} strokeWidth={1.2} lineCap="round" />

      {/* Right eye */}
      <Ellipse x={7} y={-44} radiusX={5.5} radiusY={6.5} fill="white" />
      <Circle x={8} y={-43} radius={3.5} fill={eyeColor} />
      <Circle x={8.5} y={-43.5} radius={2} fill={pupilColor} />
      <Circle x={9.5} y={-45} radius={1.2} fill="white" />
      {/* Right eyelash */}
      <Line points={[12, -49, 10, -47, 7, -50]} stroke={hairDark} strokeWidth={1.2} lineCap="round" />

      {/* Eyebrows */}
      <Line points={[-11, -50, -7, -52, -3, -50]} stroke={hairDark} strokeWidth={1} lineCap="round" />
      <Line points={[3, -50, 7, -52, 11, -50]} stroke={hairDark} strokeWidth={1} lineCap="round" />

      {/* Nose */}
      <Line points={[0, -41, 1, -38]} stroke={skinShadow} strokeWidth={1.5} lineCap="round" />

      {/* Smile */}
      <Arc x={0} y={-36} innerRadius={0} outerRadius={4} angle={180} rotation={0} fill={lipColor} />

      {/* Cheeks */}
      <Ellipse x={-12} y={-38} radiusX={4} radiusY={2.5} fill={cheekColor} opacity={0.35} />
      <Ellipse x={12} y={-38} radiusX={4} radiusY={2.5} fill={cheekColor} opacity={0.35} />
    </Group>
  );
});
